import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  getSources,
  createSource,
  updateSource,
  deleteSource,
  syncSource,
  seedSources,
  type KnowledgeSource,
} from "@/lib/api";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn, formatDateTime } from "@/lib/utils";
import {
  Loader2,
  Plus,
  Trash2,
  ExternalLink,
  BookOpen,
  MessageSquare,
  Layers,
  FileText,
  Database,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Play,
} from "lucide-react";

const sourceTypeConfig = {
  help: {
    label: "Help center",
    icon: BookOpen,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  slack: {
    label: "Slack",
    icon: MessageSquare,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
  },
  linear: {
    label: "Linear",
    icon: Layers,
    color: "text-indigo-500",
    bgColor: "bg-indigo-500/10",
  },
  notion: {
    label: "Notion",
    icon: FileText,
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
  },
};

const sourceTypes = ["help", "slack", "linear", "notion"] as const;

type SourceType = (typeof sourceTypes)[number];

function getSyncStatusBadge(status: string, error?: string | null) {
  switch (status) {
    case "synced":
      return (
        <Badge variant="success" className="text-[10px] h-5">
          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
          Synced
        </Badge>
      );
    case "syncing":
      return (
        <Badge className="bg-blue-100 text-blue-800 text-[10px] h-5">
          <Loader2 className="h-2.5 w-2.5 animate-spin mr-0.5" />
          Syncing
        </Badge>
      );
    case "error":
      return (
        <Badge variant="destructive" className="text-[10px] h-5" title={error || undefined}>
          <XCircle className="h-2.5 w-2.5 mr-0.5" />
          Error
        </Badge>
      );
    case "pending":
    default:
      return (
        <Badge variant="secondary" className="text-[10px] h-5">
          <Clock className="h-2.5 w-2.5 mr-0.5" />
          Pending
        </Badge>
      );
  }
}

export function SourcesPage() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<SourceType | "all">("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state for new source
  const [newSource, setNewSource] = useState<{
    source_type: SourceType;
    source_id: string;
    name: string;
    url: string;
    // Notion-specific fields
    notionPageUrlOrId: string;
    includeChildren: boolean;
  }>({
    source_type: "help",
    source_id: "",
    name: "",
    url: "",
    notionPageUrlOrId: "",
    includeChildren: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["sources"],
    queryFn: getSources,
    refetchInterval: 10000,
  });

  const createMutation = useMutation({
    mutationFn: createSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
      setIsAddDialogOpen(false);
      setNewSource({
        source_type: "help",
        source_id: "",
        name: "",
        url: "",
        notionPageUrlOrId: "",
        includeChildren: true,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateSource>[1] }) =>
      updateSource(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
      setDeleteConfirmId(null);
    },
  });

  const syncMutation = useMutation({
    mutationFn: syncSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  const seedMutation = useMutation({
    mutationFn: seedSources,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sources"] });
    },
  });

  const filteredSources = data?.sources.filter(
    (s) => selectedType === "all" || s.source_type === selectedType
  ) ?? [];

  const sourceCountsByType = sourceTypes.reduce((acc, type) => {
    acc[type] = data?.sources.filter((s) => s.source_type === type).length ?? 0;
    return acc;
  }, {} as Record<SourceType, number>);

  const handleToggleEnabled = (source: KnowledgeSource) => {
    updateMutation.mutate({
      id: source.id,
      data: { enabled: !source.enabled },
    });
  };

  const handleSync = (id: string) => {
    syncMutation.mutate(id);
  };

  // Extract Notion page ID from URL or use raw ID
  const extractNotionPageId = (urlOrId: string): string => {
    // If it's a URL, extract the page ID from the end
    const urlMatch = urlOrId.match(/([a-f0-9]{32})(?:[?#]|$)/i);
    if (urlMatch) {
      return urlMatch[1];
    }
    // If it looks like a UUID with dashes, remove them
    const uuidMatch = urlOrId.match(/([a-f0-9-]{36})/i);
    if (uuidMatch) {
      return uuidMatch[1].replace(/-/g, '');
    }
    // Return as-is (might be a raw 32-char ID)
    return urlOrId.replace(/-/g, '');
  };

  const handleCreate = () => {
    if (newSource.source_type === 'notion') {
      // For Notion sources, use the page URL/ID
      if (!newSource.notionPageUrlOrId || !newSource.name) return;
      const pageId = extractNotionPageId(newSource.notionPageUrlOrId);
      createMutation.mutate({
        source_type: 'notion',
        source_id: pageId,
        name: newSource.name,
        url: newSource.notionPageUrlOrId.startsWith('http')
          ? newSource.notionPageUrlOrId
          : `https://www.notion.so/${pageId}`,
        config: {
          pageUrlOrId: newSource.notionPageUrlOrId,
          includeChildren: newSource.includeChildren,
        },
      });
    } else {
      // For other source types
      if (!newSource.source_id || !newSource.name) return;
      createMutation.mutate({
        source_type: newSource.source_type,
        source_id: newSource.source_id,
        name: newSource.name,
        url: newSource.url || undefined,
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Knowledge Sources</h1>
            <p className="text-muted-foreground text-sm">
              Manage data sources for the RAG pipeline
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isPending}
            >
              {seedMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Database className="h-3 w-3 mr-1" />
              )}
              Seed Defaults
            </Button>
            <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="h-3 w-3 mr-1" />
              Add Source
            </Button>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Knowledge Source</DialogTitle>
                  <DialogDescription>
                    Add a new data source to sync into the RAG pipeline.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Source Type</Label>
                    <Select
                      value={newSource.source_type}
                      onValueChange={(v) =>
                        setNewSource({ ...newSource, source_type: v as SourceType })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sourceTypes.map((type) => {
                          const config = sourceTypeConfig[type];
                          return (
                            <SelectItem key={type} value={type}>
                              <div className="flex items-center gap-2">
                                <config.icon className={cn("h-4 w-4", config.color)} />
                                {config.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Notion-specific fields */}
                  {newSource.source_type === "notion" ? (
                    <>
                      <div className="space-y-2">
                        <Label>Notion Page URL or ID</Label>
                        <Input
                          placeholder="https://www.notion.so/workspace/Page-Title-abc123... or just the page ID"
                          value={newSource.notionPageUrlOrId}
                          onChange={(e) =>
                            setNewSource({ ...newSource, notionPageUrlOrId: e.target.value })
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Paste the full Notion page URL or just the 32-character page ID
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Display Name</Label>
                        <Input
                          placeholder="e.g., Customer Ops Guide"
                          value={newSource.name}
                          onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <Label>Include Child Pages</Label>
                          <p className="text-xs text-muted-foreground">
                            Recursively sync all nested pages under this page
                          </p>
                        </div>
                        <Switch
                          checked={newSource.includeChildren}
                          onCheckedChange={(checked) =>
                            setNewSource({ ...newSource, includeChildren: checked })
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Non-Notion source fields */}
                      <div className="space-y-2">
                        <Label>Source ID</Label>
                        <Input
                          placeholder={
                            newSource.source_type === "slack"
                              ? "Channel ID (e.g., C03HDQUJPT7)"
                              : "URL slug (e.g., merchant-portal-refunds)"
                          }
                          value={newSource.source_id}
                          onChange={(e) =>
                            setNewSource({ ...newSource, source_id: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Display Name</Label>
                        <Input
                          placeholder="e.g., Customer Ops Channel"
                          value={newSource.name}
                          onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>URL (optional)</Label>
                        <Input
                          placeholder="https://..."
                          value={newSource.url}
                          onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={
                      createMutation.isPending ||
                      !newSource.name ||
                      (newSource.source_type === "notion"
                        ? !newSource.notionPageUrlOrId
                        : !newSource.source_id)
                    }
                  >
                    {createMutation.isPending && (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    )}
                    Add Source
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-5">
          <Card
            className={cn(
              "cursor-pointer transition-all",
              selectedType === "all" && "ring-2 ring-primary"
            )}
            onClick={() => setSelectedType("all")}
          >
            <CardHeader className="p-3 pb-1">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                All sources
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="text-2xl font-bold">{data?.total ?? 0}</div>
            </CardContent>
          </Card>
          {sourceTypes.map((type) => {
            const config = sourceTypeConfig[type];
            const Icon = config.icon;
            return (
              <Card
                key={type}
                className={cn(
                  "cursor-pointer transition-all",
                  selectedType === type && "ring-2 ring-primary"
                )}
                onClick={() => setSelectedType(type)}
              >
                <CardHeader className="p-3 pb-1">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <Icon className={cn("h-3.5 w-3.5", config.color)} />
                    {config.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="text-2xl font-bold">{sourceCountsByType[type]}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSources.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No sources found</p>
                <p className="text-sm">Add a source or seed defaults to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Enabled</TableHead>
                    <TableHead className="w-[80px]">Type</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[150px]">Last Synced</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSources.map((source) => {
                    const config = sourceTypeConfig[source.source_type];
                    const Icon = config.icon;
                    return (
                      <TableRow key={source.id}>
                        <TableCell>
                          <Switch
                            checked={source.enabled}
                            onCheckedChange={() => handleToggleEnabled(source)}
                            disabled={updateMutation.isPending}
                          />
                        </TableCell>
                        <TableCell>
                          <div
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
                              config.bgColor,
                              config.color
                            )}
                          >
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{source.name}</span>
                            <span className="text-[10px] text-muted-foreground font-mono">
                              {source.source_id}
                            </span>
                            {source.source_type === "notion" && source.config && (
                              <span className="text-[10px] text-muted-foreground">
                                {(source.config as { includeChildren?: boolean }).includeChildren
                                  ? "Including child pages"
                                  : "Page only"}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getSyncStatusBadge(source.sync_status, source.sync_error)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {source.last_synced_at
                            ? formatDateTime(source.last_synced_at)
                            : "Never"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {source.url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                asChild
                              >
                                <a href={source.url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleSync(source.id)}
                              disabled={
                                !source.enabled ||
                                source.sync_status === "syncing" ||
                                syncMutation.isPending
                              }
                              title="Sync now"
                            >
                              {source.sync_status === "syncing" ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={() => setDeleteConfirmId(source.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Source</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this source? This action cannot be undone.
                The associated vectors will remain in the index until purged separately.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending && (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                )}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
